import React from 'react';
import { injectIntl, FormattedNumber, FormattedList } from 'react-intl';
import { connect } from 'react-redux';
import { Label, Tooltip } from 'react-bootstrap';
import { find as _find } from 'lodash';

import { getNameInCurrentLanguage } from 'helpers/intl';

import OverlayTrigger from 'components/OverlayTrigger';

import IntlDateTime from 'components/layout/IntlDateTime';
import { getOrderAuthorizationStatus, isAnyOrderItemOfType } from 'helpers/order';
import { confirmAlert } from 'react-confirm-alert';
import { addNotification } from 'actions/NotificationsActions';
import axios from 'axios';
import {
  isUserAbleToStartPipeline,
  logStartPipelineButtonClicked,
  removeStartPipelineButtonClickedLog,
} from 'helpers/leads';

class LeadProgress extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      updatingStageStatus: false,
      entity: props.entity
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.entityLoaded !== this.state.entityLoaded || this.props.entity !== this.state.entity) {
      this.setState({
        entity: this.props.entity,
        entityLoaded: this.props.entityLoaded,
        currentStage: this.props.entity.getCurrentStage(),
        currentStageStatus: this.props.entity.getCurrentStageStatus(),
      });
    }
  }

  moveLead = (e, stageId) => {
    e.preventDefault();
    const { intl: { messages }, doNotify, moveLead, entity } = this.props;
    const { currentStage, currentStageStatus } = this.state;

    if(isAnyOrderItemOfType(entity.getOrders()[0] ?? null, 'DOMAIN_HOSTING')){
      if(currentStageStatus === 'in_progress' || !getOrderAuthorizationStatus(entity.getOrders()[0] ?? null, 'aws_domain_hosting')) {
        doNotify(0, messages.lead_stage_change_error_domain_hosting_in_progress);
        return;
      }

      if(currentStageStatus === 'not_started' && !isUserAbleToStartPipeline(entity.getId())) {
        doNotify(0, messages.lead_stage_change_error_domain_hosting_command_to_start_pipeline_has_been_sent);
        return;
      }

      confirmAlert({
        title: messages.lead_stage_change_confirm_title,
        message: messages.lead_stage_change_confirm_body,
        buttons: [
          {
            label: messages.yes,
            onClick: () => {
              moveLead(stageId);
              logStartPipelineButtonClicked(entity.getId())
              doNotify(1, messages.pipeline_started);
              // update state to immediately take effect to the view
              this.setState({ startPipelineButtonClickedTime: new Date().getTime() })
            },
          },
          {
            label: messages.no,
            onClick: () => undefined,
          },
        ],
        closeOnEscape: true,
        closeOnClickOutside: true,
      });
    } else {
      moveLead(stageId);
    }
  };

  // block lead manipulation when the lead is for order
  // only exception is when it's a car rent order
  isOrderLead = () => {
    const { entityLoaded, entity } = this.props;
    if (entityLoaded) {
      const orders = entity.getOrders();
      if (orders && orders.length) {
        const orderLeadId = orders[0].getLeadId();
        return orders[0].getType() !== 'car_rent' && entity.getId() === orderLeadId;
      }
    }

    return false;
  };

  intervalId = null;
  componentDidMount() {
    // Fetch the initial stage
    this.fetchPipelineStage();

    // Set up the interval to fetch the stage every 10 seconds
    this.intervalId = setInterval(this.fetchPipelineStage, 10000);
  }

  componentWillUnmount() {
    // Clear the interval when the component unmounts
    if(this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  doNothing = e => {
    e.preventDefault();
  };

  fetchPipelineStage = () => {
    try {
      const { entity } = this.props;
      const { entityLoaded, currentStage, currentStageStatus } = this.state;

      if(entity && entityLoaded &&
        isAnyOrderItemOfType(entity.getOrders()[0] ?? null, 'DOMAIN_HOSTING') &&
        getOrderAuthorizationStatus(entity.getOrders()[0] ?? null, 'aws_domain_hosting')) {

        const entityId = this.state.entity.getId();

        if(isUserAbleToStartPipeline(entityId) ) {
            removeStartPipelineButtonClickedLog(entityId);
        }

        axios.get('/api/domainregistration/leads/' + this.state.entity.getId() + '/currentstage.json')
          .then(response => {
            let entity = this.state.entity;
            if (currentStage !== response.data.currentStage || currentStageStatus !== response.data.currentStageStatus) {
              removeStartPipelineButtonClickedLog(entity.getId())
              this.setState({
                currentStage: response.data.currentStage,
                currentStageStatus: response.data.currentStageStatus,
              }, () => {
                console.log('Updated stage status: ' + currentStageStatus);
              });
            }
          })
      }
    } catch (err) {
      this.setState({ error: err, loading: false });
    }
  };

  render() {
    const {
      intl: { messages },
      appIntl,
      entity
    } = this.props;
    const { entityLoaded, currentStage, currentStageStatus  } = this.state;

    if (entityLoaded) {
      const disabled = entityLoaded && (entity.getIsWon() || entity.getIsLost());
      const isOrderLead = this.isOrderLead();
      const pipeline = entity.getPipeline();
      const stages = pipeline.getStages();
      const isDomainHosting = isAnyOrderItemOfType(entity.getOrders()[0] ?? null, 'DOMAIN_HOSTING');
      let currentStageIndex = 0;
      for (let i = 0; i < stages.length; i++) {
        if (stages[i].getId() === currentStage) {
          currentStageIndex = i;
          break;
        }
      }

      return (
        <div className="bs-wizard clearfix">
          {!!stages &&
            stages.map((stage, i) => {
              const isCurrent = i === currentStageIndex;
              const isLastStageCurrent = i === currentStageIndex && i === stages.length - 1;
              // if the Pipeline is on the last stage, consider it as completed for UI
              const isComplete = i < currentStageIndex || entity.getIsWon() || entity.getIsLost() || isLastStageCurrent;
              const stageTimeEntity = _find(entity.data.stage_time, { id: stage.getId() });
              const nowDateString = new Date().toISOString();
              const stageTime =
                isComplete && !stageTimeEntity
                  ? { time: 5, min_date: nowDateString, max_date: nowDateString }
                  : stageTimeEntity;
              const isSkipped = !stageTime;
              const name = getNameInCurrentLanguage(stage.getName(), appIntl);

              let iconClass = 'fas fa-circle';
              let labelStyle;
              let label = messages.pending;
              let tooltip = null;
              if (isCurrent && (!isDomainHosting || currentStageStatus === 'in_progress' || currentStageStatus === '') || (currentStageStatus === 'not_started' && !isUserAbleToStartPipeline(entity.getId()))) {
                iconClass = 'fas fa-user-tie';
                labelStyle = 'primary';
                label = messages.in_progress;
              }

              if (isCurrent && isDomainHosting && currentStageStatus === 'failed') {
                iconClass = 'fas fa-repeat';
                labelStyle = 'primary';
                label = messages.failed;
              }

              if (isCurrent && isDomainHosting && currentStageStatus === 'not_started' && isUserAbleToStartPipeline(entity.getId())) {
                iconClass = 'fas fa-play';
                labelStyle = 'primary';
                label = messages.failed;
              }

              if (isCurrent && isDomainHosting && currentStageStatus === 'completed') {
                iconClass = 'fas fa-check';
                labelStyle = 'primary';
                label = messages.failed;
              }

              if (isComplete) {
                iconClass = 'fas fa-check';
                labelStyle = 'success';
                label = messages.finished;
              }
              if (isComplete && isSkipped) {
                iconClass = 'fas fa-forward';
                label = messages.skipped;
              }

              if (!isSkipped) {
                const stageMinutes = stageTime.time / 60;
                const stageHours = stageMinutes / 60;
                const stageDays = stageHours / 24;

                const totalTimeElements = [];
                if (stageDays > 1) {
                  totalTimeElements.push(
                    <FormattedNumber
                      value={Math.floor(stageDays)}
                      style="unit"
                      unit="day"
                      key="day"
                      unitDisplay="long"
                    />
                  );
                }
                if (stageHours > 1) {
                  totalTimeElements.push(
                    <FormattedNumber
                      value={Math.floor(stageHours % 24)}
                      style="unit"
                      unit="hour"
                      key="hour"
                      unitDisplay="long"
                    />
                  );
                }
                totalTimeElements.push(
                  <FormattedNumber
                    value={Math.floor(stageMinutes % 60)}
                    style="unit"
                    unit="minute"
                    key="minute"
                    unitDisplay="long"
                  />
                );

                tooltip = (
                  <div className="text-left">
                    <i className="fas fa-asterisk fa-fw" /> <IntlDateTime date={stageTime.min_date} />
                    {stageTime.min_date !== stageTime.max_date && (
                      <>
                        <br />
                        <i className="fas fa-history fa-fw" /> <IntlDateTime date={stageTime.max_date} />
                      </>
                    )}
                    <br />
                    <i className="fas fa-stopwatch fa-fw" />{' '}
                    <FormattedList type="conjunction" value={totalTimeElements} />
                  </div>
                );
              }

              return (
                <div
                  key={stage.getId()}
                  className={
                    'bs-wizard-step pull-left ' + (isCurrent ? 'current ' : '') + (isComplete ? 'complete ' : '')
                  }
                  style={{ width: 100 / stages.length + '%' }}
                >
                  <div className="progress">
                    <div className="pre-progress-bar" />
                    <div className="post-progress-bar" />
                  </div>
                  {(isCurrent && (!isDomainHosting || currentStageStatus === 'in_progress')) || disabled ? ( //|| isOrderLead ? (
                    <a href={'#' + name} className="bs-wizard-dot" onClick={this.doNothing}>
                      <i className={`icon ${iconClass}`} />
                    </a>
                  ) : (
                    <a href={'#' + name} className="bs-wizard-dot" onClick={e => this.moveLead(e, stage.id)}>
                      <i className={`icon ${iconClass}`} />
                    </a>
                  )}
                  <div className="bs-wizard-stepnum">
                    <span>
                      <h6 className="bold overflow-ellipsis" title={name}>
                        {name}
                      </h6>
                      <Label bsStyle={labelStyle}>
                        {label}
                        {!!tooltip && (
                          <>
                            {' '}
                            <OverlayTrigger placement="top" wrappingComponent={Tooltip} overlay={tooltip}>
                              <i className="fas fa-question-circle" />
                            </OverlayTrigger>
                          </>
                        )}
                      </Label>
                    </span>
                  </div>
                </div>
              );
            })}
        </div>
      );
    }

    return null;
  }
}

const mapDispatchToProps = dispatch => {
  return {
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
  };
};

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
  };
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(LeadProgress));
